import { defineStore } from 'pinia'

import menuDataJSON from '~/data/menu.json'
import brandSectionJSON from '~/data/brandSection.json'
import sortMenuQueryByPriority from '~/utils/prepareMenuData'
import sortByPriority from '~/utils/sortByPriority'
import type { Maybe, Query, Sds_PageList, Sg_MenuLink } from '~/schema/types/generated'

interface IBrandSectionBrand {
    src: string
    alt: string
    url: string
}

export const useMenuStore = defineStore(
    'menu',
    () => {
        const isMenuMobileOpen = ref(false)
        const isMenuDesktopOpen = ref(false)

        function toggleMobileMenu() {
            isMenuMobileOpen.value = !isMenuMobileOpen.value
        }
        function closeMobileMenu() {
            isMenuMobileOpen.value = false
        }
        function handleDesktopMenu(val: boolean) {
            isMenuDesktopOpen.value = val
        }

        const isIplusClosed = ref(true)
        const isIplusHiddenOnScroll = ref(false)
        const isIplusVisible = computed(() => !isIplusClosed.value && !isIplusHiddenOnScroll.value)

        function setIsIplusClosed(val: boolean) {
            isIplusClosed.value = val
        }

        function setIsIplusHiddenOnScroll(val: boolean) {
            isIplusHiddenOnScroll.value = val
        }

        const menuData = ref<Maybe<Maybe<Sg_MenuLink>[]> | undefined>()
        const brandSectionData =
            ref<Maybe<Pick<Sds_PageList, 'name' | 'priority' | 'isActive' | 'imgUrl' | 'title'>[]>>()

        function getBrandSectionData(): IBrandSectionBrand[] {
            // prepare structure for brand section component
            // TODO - unify brand section and menu brand section data structure

            return (
                brandSectionData.value?.map(({ imgUrl, name }) => {
                    return {
                        src: imgUrl ?? '',
                        alt: name ?? '',
                        url: `/lp/strefa-dobrych-sprzetow/${name}`,
                    }
                }) ?? []
            )
        }

        async function fetchMenuData() {
            try {
                const menuDataResponse = await $fetch<{
                    data: Query
                }>('/api/cache/prometheus/menu', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        variables: {},
                    }),
                })

                if (!menuDataResponse) {
                    console.error('FETCH MENU DATA ERROR:', menuDataResponse)
                    return
                }
                menuData.value = sortMenuQueryByPriority(menuDataResponse.data).sg_menulinks
            } catch (error) {
                console.error('FETCH MENU DATA ERROR:', error)
                menuData.value = JSON.parse(JSON.stringify(menuDataJSON)).data.sg_menulinks
            }
        }
        async function fetchBrandSectionData() {
            try {
                const brandSectionDataResponse = await $fetch<{
                    data: {
                        sds_pagelists: Sds_PageList[]
                    }
                }>('/api/cache/prometheus/brand_section', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        variables: {},
                    }),
                })

                if (!brandSectionDataResponse) {
                    console.error('FETCH MENU BRAND SECTION ERROR:', brandSectionDataResponse)
                    return
                }

                brandSectionData.value = sortByPriority([...brandSectionDataResponse.data.sds_pagelists])
            } catch (error) {
                console.error('FETCH MENU BRAND SECTION ERROR:', error)
                const data = JSON.parse(JSON.stringify(brandSectionJSON)).data
                brandSectionData.value = sortByPriority(data.sds_pagelists)
            }
        }
        async function fetchAllMenuData() {
            await Promise.all([fetchMenuData(), fetchBrandSectionData()])
        }

        return {
            isMenuMobileOpen,
            isMenuDesktopOpen,
            isIplusClosed,
            setIsIplusClosed,
            isIplusHiddenOnScroll,
            setIsIplusHiddenOnScroll,
            isIplusVisible,
            toggleMobileMenu,
            closeMobileMenu,
            handleDesktopMenu,

            // menu data
            menuData,
            brandSectionData,
            getBrandSectionData,
            fetchMenuData,
            fetchBrandSectionData,
            fetchAllMenuData,
        }
    },
    {
        persist: [
            {
                key: 'isIplusClosed',
                paths: ['isIplusClosed'],
            },
        ],
    },
)
